/**
 * Cycle Bundle Builder
 *
 * State lives in a Map keyed by variant id. Every change re-renders the cards'
 * steppers, the summary list, totals and both CTAs from that one source.
 *
 * Discount rules come from each step's data attributes (set in the theme
 * editor): none, flat (X off each) or tiered (tier 1 / tier 2 off each once
 * N products are chosen). Discounts are per unit and never exceed the price.
 *
 * Add to cart sends every line in ONE /cart/add.js request, each tagged with
 * hidden properties so a checkout discount can apply the same amounts:
 *   _bundle_id, _bundle_name, _bundle_step, _bundle_discount (cents per unit)
 */
(() => {
  if (window.customElements.get('cycle-bundle-builder')) return;

  const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class CycleBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const d = this.dataset;
      this.moneyFormat = d.moneyFormat || '${{amount}}';
      this.maxQty = parseInt(d.maxQty, 10) || 10;
      this.text = {
        unitOne: d.unitSingular || '',
        unitMany: d.unitPlural || d.unitSingular || '',
        saving: d.savingText || '',
        totalSaving: d.totalSavingText || '',
        selected: d.selectedText || '',
        hint: d.hintText || '',
        discountLine: d.discountLineText || '',
        required: d.requiredText || '',
        badge: d.badgeText || '',
        cta: d.ctaLabel || 'Add bundle to cart',
        adding: d.addingLabel || 'Adding…',
        added: d.addedLabel || 'Added ✓',
      };

      this.steps = Array.from(this.querySelectorAll('[data-step]')).map((el) => ({
        el,
        index: parseInt(el.dataset.stepIndex, 10),
        title: el.dataset.stepTitle || '',
        required: el.dataset.required === 'true',
        type: el.dataset.discountType || 'none',
        flat: parseInt(el.dataset.flat, 10) || 0,
        t1Min: parseInt(el.dataset.t1Min, 10) || 0,
        t1: parseInt(el.dataset.t1, 10) || 0,
        t2Min: parseInt(el.dataset.t2Min, 10) || 0,
        t2: parseInt(el.dataset.t2, 10) || 0,
        countMode: el.dataset.countMode || 'products',
        one: el.dataset.labelSingular || '',
        many: el.dataset.labelPlural || el.dataset.labelSingular || '',
        discountLabel: el.dataset.discountLabel || '',
        hintEl: el.querySelector('[data-step-hint]'),
        countEl: el.querySelector('[data-step-count]'),
      }));
      this.stepByIndex = new Map(this.steps.map((s) => [s.index, s]));

      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.items = new Map(); // variantId -> item
      this.order = 0;

      this.listEl = this.querySelector('[data-list]');
      this.emptyEl = this.querySelector('[data-empty]');
      this.ctaEl = this.querySelector('[data-cta]');
      this.ctaTextEl = this.querySelector('[data-cta-text]');
      this.msgEl = this.querySelector('[data-msg]');
      this.barEl = this.querySelector('[data-bar]');
      this.barCtaEl = this.querySelector('[data-bar-cta]');

      this.bindCards();
      this.bindSummary();
      this.bindShowMore();
      this.bindStepToggles();
      this.bindStickyBar();
      this.render();
    }

    // ---------------------------------------------------------------- Cards

    bindCards() {
      // Every variant on the page, so a quantity can be set by variant id no
      // matter which variant its card is currently showing. Quantities are
      // keyed by variant — two flavours of one product are two box lines.
      this.variants = new Map();
      this.cards.forEach((card) => {
        const select = card.querySelector('[data-variant-select]');
        const base = {
          card,
          step: parseInt(card.dataset.stepIndex, 10),
          productId: card.dataset.productId,
          title: card.dataset.title || '',
          subtitle: card.dataset.subtitle || '',
        };
        if (select) {
          Array.from(select.options).forEach((option) => {
            this.variants.set(String(option.value), Object.assign({}, base, {
              price: parseInt(option.dataset.price, 10) || 0,
              available: option.dataset.available === 'true',
              preorder: option.dataset.preorder === 'true',
              variantTitle: option.dataset.title || '',
              image: option.dataset.image || card.dataset.image || '',
            }));
          });
        } else {
          this.variants.set(String(card.dataset.variantId), Object.assign({}, base, {
            price: parseInt(card.dataset.price, 10) || 0,
            available: card.dataset.available === 'true',
            preorder: card.dataset.preorder === 'true',
            variantTitle: card.dataset.variantTitle || '',
            image: card.dataset.image || '',
          }));
        }
      });

      this.cards.forEach((card) => {
        const input = card.querySelector('[data-card-qty]');
        const minus = card.querySelector('[data-card-minus]');
        const plus = card.querySelector('[data-card-plus]');
        const select = card.querySelector('[data-variant-select]');
        const current = () => card.dataset.variantId;

        // Clicking the card body selects the shown variant (1) or clears it (0).
        card.addEventListener('click', (event) => {
          if (event.target.closest('[data-no-toggle], select, a, button, input')) return;
          if (card.dataset.available !== 'true') return;
          this.setQtyFor(current(), this.qtyOf(current()) > 0 ? 0 : 1);
        });

        minus.addEventListener('click', () => this.setQtyFor(current(), this.qtyOf(current()) - 1));
        plus.addEventListener('click', () => this.setQtyFor(current(), this.qtyOf(current()) + 1));
        input.addEventListener('change', () => this.setQtyFor(current(), parseInt(input.value, 10) || 0));
        input.addEventListener('focus', () => input.select());

        // Switching the picker only changes which variant the card shows. The
        // box keeps whatever it had; the stepper shows this variant's own qty.
        if (select) {
          select.addEventListener('change', () => {
            const v = this.variants.get(String(select.value));
            if (!v) return;
            card.dataset.variantId = select.value;
            card.dataset.price = v.price;
            card.dataset.available = v.available ? 'true' : 'false';
            card.dataset.variantTitle = v.variantTitle;
            if (v.image) {
              card.dataset.image = v.image;
              const img = card.querySelector('.cbb-card__media img');
              if (img && img.dataset.variantSwap !== v.image) {
                img.removeAttribute('srcset');
                img.src = v.image.replace(/width=\d+/, 'width=300');
                img.dataset.variantSwap = v.image;
              }
            }
            card.dataset.preorder = v.preorder ? 'true' : 'false';
            const preBadge = card.querySelector('[data-preorder-badge]');
            if (preBadge) preBadge.hidden = !v.preorder;
            const soldBadge = card.querySelector('[data-soldout-badge]');
            if (soldBadge) soldBadge.hidden = !!v.available;
            card.classList.toggle('is-soldout', !v.available);
            input.disabled = !v.available;
            this.render();
          });
        }
      });
    }

    qtyOf(variantId) {
      const item = this.items.get(String(variantId));
      return item ? item.qty : 0;
    }

    setQtyFor(variantId, qty) {
      const id = String(variantId);
      const v = this.variants.get(id);
      const existing = this.items.get(id);
      qty = Math.max(0, Math.min(this.maxQty, qty));
      if (qty > 0 && v && !v.available) qty = 0;

      if (qty === 0) {
        this.items.delete(id);
      } else if (existing) {
        existing.qty = qty;
      } else if (v) {
        this.items.set(id, {
          id,
          step: v.step,
          productId: v.productId,
          title: v.title,
          subtitle: v.subtitle,
          variantTitle: v.variantTitle,
          preorder: !!v.preorder,
          image: v.image,
          price: v.price,
          qty,
          order: ++this.order,
        });
      }
      this.setMessage('');
      this.render();
    }

    // -------------------------------------------------------------- Summary

    bindSummary() {
      this.listEl.addEventListener('click', (event) => {
        const line = event.target.closest('[data-line]');
        if (!line) return;
        const id = line.dataset.line;
        const item = this.items.get(id);
        if (!item) return;
        let qty = item.qty;
        if (event.target.closest('[data-line-remove]')) qty = 0;
        else if (event.target.closest('[data-line-minus]')) qty -= 1;
        else if (event.target.closest('[data-line-plus]')) qty += 1;
        else return;
        this.setQtyFor(id, qty);
      });
      this.listEl.addEventListener('change', (event) => {
        const input = event.target.closest('[data-line-qty]');
        if (!input) return;
        this.setQtyFor(input.closest('[data-line]').dataset.line, parseInt(input.value, 10) || 0);
      });

      this.ctaEl.addEventListener('click', () => this.addToCart());
      if (this.barCtaEl) this.barCtaEl.addEventListener('click', () => this.addToCart());
    }

    // Each step opens and closes from its header. With "only one step open"
    // on, opening a step closes the rest.
    bindStepToggles() {
      this.accordion = this.dataset.accordion === 'true';
      this.steps.forEach((step) => {
        const head = step.el.querySelector('[data-step-toggle]');
        if (!head) return;
        head.addEventListener('click', (event) => {
          if (event.target.closest('a')) return;
          this.toggleStep(step, step.el.classList.contains('is-closed'));
        });
      });
    }

    toggleStep(step, open) {
      if (open && this.accordion) {
        this.steps.forEach((other) => { if (other !== step) this.setStepOpen(other, false); });
      }
      this.setStepOpen(step, open);
    }

    setStepOpen(step, open) {
      step.el.classList.toggle('is-closed', !open);
      const chevron = step.el.querySelector('[data-step-chevron]');
      if (chevron) chevron.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    bindShowMore() {
      this.querySelectorAll('[data-show-more]').forEach((btn) => {
        const grid = btn.closest('[data-step]').querySelector('[data-grid]');
        const moreLabel = btn.textContent;
        btn.addEventListener('click', () => {
          const collapsed = grid.classList.toggle('is-collapsed');
          btn.textContent = collapsed ? moreLabel : btn.dataset.less || 'Show less';
          if (collapsed) {
            this.measureHeader();
            grid.closest('[data-step]').scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });
    }

    // Mobile bar shows while the summary's CTA is out of view.
    bindStickyBar() {
      if (!this.barEl || !('IntersectionObserver' in window)) return;
      this.ctaVisible = false;
      this.inSection = false;
      const update = () => {
        const show = this.inSection && !this.ctaVisible && this.items.size > 0;
        this.barEl.classList.toggle('is-visible', show);
        this.barEl.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (this.barCtaEl) this.barCtaEl.tabIndex = show ? 0 : -1;
        if (show !== this.barShown) {
          this.barShown = show;
          // wait for the slide-in so the bar's real height is measured
          setTimeout(() => this.liftWidgets(), show ? 260 : 0);
        }
      };
      this.bindWidgetLift();
      this.updateBar = update;
      new IntersectionObserver((entries) => {
        this.ctaVisible = entries[0].isIntersecting;
        update();
      }).observe(this.ctaEl);
      new IntersectionObserver((entries) => {
        this.inSection = entries[0].isIntersecting;
        update();
      }).observe(this.querySelector('.cbb__main'));
    }

    // Floating widgets (chat, accessibility, back-to-top, popups…) sit in the
    // bottom corners and cover the sticky bar on phones. While the bar shows,
    // nudge any small fixed element in the bottom area up by the bar's height,
    // and put it back when the bar hides. Uses `translate` so the widget's
    // own `transform` is left alone.
    bindWidgetLift() {
      this.lifted = new Map();
      let queued = false;
      const queue = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          this.liftWidgets();
        });
      };
      // Widgets often load late or resize themselves; re-check while the bar is up.
      new MutationObserver(() => { if (this.barShown) queue(); })
        .observe(document.body, { childList: true });
      window.addEventListener('resize', queue, { passive: true });
      let lastScrollCheck = 0;
      window.addEventListener('scroll', () => {
        if (!this.barShown) return;
        const now = Date.now();
        if (now - lastScrollCheck < 600) return;
        lastScrollCheck = now;
        queue();
      }, { passive: true });
      setInterval(() => { if (this.barShown) queue(); }, 1500);
    }

    // Fixed-position elements near the top of the DOM, outside this builder.
    fixedElements() {
      const found = [];
      const walk = (el, depth) => {
        for (const child of el.children) {
          if (child === this || this.contains(child)) continue;
          if (/^(SCRIPT|STYLE|LINK|META|NOSCRIPT|TEMPLATE)$/.test(child.tagName)) continue;
          const pos = getComputedStyle(child).position;
          if (pos === 'fixed' || pos === 'sticky') found.push([child, pos]);
          // Keep looking inside wrappers: some widgets are a fixed 0-height
          // overlay holding the real (fixed) button.
          if (depth < 4 && !child.contains(this)) walk(child, depth + 1);
        }
      };
      walk(document.body, 0);
      return found;
    }

    liftWidgets() {
      if (!this.lifted) return;
      const barH = this.barShown ? Math.round(this.barEl.getBoundingClientRect().height) : 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const keep = new Set();
      if (barH > 0) {
        this.fixedElements().forEach(([el, pos]) => {
          if (pos !== 'fixed') return;
          const r = el.getBoundingClientRect();
          if (!r.width || !r.height) return;
          if (r.right <= 0 || r.left >= vw) return; // off-screen (closed drawers)
          if (r.width > vw * 0.6 || r.height > vh * 0.5) return; // drawers, overlays, full-width bars
          for (const k of keep) if (k.contains(el)) return; // parent already moved
          const applied = this.lifted.get(el) || 0;
          const bottom = r.bottom + applied; // where it sits without our nudge
          const top = r.top + applied;
          if (bottom < vh - barH - 4 || top < vh * 0.4) return; // not in the bar's area
          keep.add(el);
          if (applied !== barH) {
            if (!this.lifted.has(el)) {
              el.dataset.cbbTranslate = el.style.translate || '';
              el.dataset.cbbTransition = el.style.transition || '';
              el.style.transition = (el.style.transition ? el.style.transition + ', ' : '') + 'translate .25s ease';
            }
            el.style.translate = `0 -${barH}px`;
            this.lifted.set(el, barH);
          }
        });
      }
      this.lifted.forEach((_, el) => {
        if (keep.has(el)) return;
        el.style.translate = el.dataset.cbbTranslate || '';
        const prevTransition = el.dataset.cbbTransition || '';
        setTimeout(() => { el.style.transition = prevTransition; }, 300);
        delete el.dataset.cbbTranslate;
        delete el.dataset.cbbTransition;
        this.lifted.delete(el);
      });
    }

    // Sticky/fixed headers hide a step's heading when we scroll to it.
    // Measure whatever is pinned to the top and use it as scroll margin.
    measureHeader() {
      let offset = 0;
      const vw = window.innerWidth;
      this.fixedElements().forEach(([el]) => {
        const r = el.getBoundingClientRect();
        if (r.width >= vw * 0.9 && r.top <= 2 && r.bottom > 0 && r.height < window.innerHeight * 0.5) {
          offset = Math.max(offset, Math.round(r.bottom));
        }
      });
      this.style.setProperty('--cbb-scroll-offset', offset + 'px');
    }

    // ------------------------------------------------------------- Pricing

    stepCount(step, items) {
      const lines = items.filter((i) => i.step === step.index);
      if (step.countMode === 'units') return lines.reduce((n, i) => n + i.qty, 0);
      return new Set(lines.map((i) => i.productId)).size;
    }

    perUnitDiscount(step, count) {
      if (!step || count < 1) return 0;
      if (step.type === 'flat') return step.flat;
      if (step.type === 'tiered') {
        // Take whichever reached tier is worth the most.
        let best = 0;
        if (step.t1Min > 0 && count >= step.t1Min) best = Math.max(best, step.t1);
        if (step.t2Min > 0 && count >= step.t2Min) best = Math.max(best, step.t2);
        return best;
      }
      return 0;
    }

    // Smallest tier above the current count, for the "add N more" hint.
    nextTier(step, count) {
      if (!step || step.type !== 'tiered') return null;
      const current = this.perUnitDiscount(step, count);
      const tiers = [
        { min: step.t1Min, amount: step.t1 },
        { min: step.t2Min, amount: step.t2 },
      ]
        .filter((t) => t.min > count && t.amount > current)
        .sort((a, b) => a.min - b.min);
      return tiers[0] || null;
    }

    compute() {
      const items = Array.from(this.items.values()).sort((a, b) => a.step - b.step || a.order - b.order);
      const perStep = new Map();
      this.steps.forEach((step) => {
        const count = this.stepCount(step, items);
        perStep.set(step.index, { step, count, perUnit: this.perUnitDiscount(step, count), units: 0, discount: 0 });
      });

      let subtotal = 0;
      let discount = 0;
      let units = 0;
      items.forEach((item) => {
        const info = perStep.get(item.step);
        const unitOff = info ? Math.min(info.perUnit, item.price) : 0;
        item.unitDiscount = unitOff;
        item.lineTotal = (item.price - unitOff) * item.qty;
        subtotal += item.price * item.qty;
        discount += unitOff * item.qty;
        units += item.qty;
        if (info) {
          info.units += item.qty;
          info.discount += unitOff * item.qty;
        }
      });

      const missing = this.steps.filter((s) => s.required && (perStep.get(s.index) || {}).count === 0);
      return { items, perStep, subtotal, discount, total: subtotal - discount, units, missing };
    }

    // -------------------------------------------------------------- Render

    render() {
      const state = this.compute();
      this.state = state;

      // Cards
      this.cards.forEach((card) => {
        const item = this.items.get(card.dataset.variantId);
        const qty = item ? item.qty : 0;
        card.classList.toggle('is-selected', qty > 0);
        const input = card.querySelector('[data-card-qty]');
        if (document.activeElement !== input) input.value = qty;
        card.querySelector('[data-card-minus]').disabled = qty === 0;
        const plus = card.querySelector('[data-card-plus]');
        plus.disabled = card.dataset.available !== 'true' || qty >= this.maxQty;

        const priceEl = card.querySelector('[data-card-price]');
        const price = parseInt(card.dataset.price, 10) || 0;
        priceEl.innerHTML = this.formatMoney(price);
        priceEl.classList.remove('is-discounted');
      });

      // Step "N selected" badges
      const countTpl = this.dataset.stepCountText || '';
      this.steps.forEach((step) => {
        if (!step.countEl) return;
        const units = state.items.filter((i) => i.step === step.index).reduce((n, i) => n + i.qty, 0);
        step.countEl.hidden = !countTpl || units === 0;
        step.countEl.textContent = units ? this.fill(countTpl, { count: units }) : '';
      });

      // Step hints
      this.steps.forEach((step) => {
        if (!step.hintEl) return;
        const info = state.perStep.get(step.index);
        const next = this.nextTier(step, info.count);
        step.hintEl.textContent = next && info.count > 0 ? this.hintFor(step, info.count, next) : '';
      });

      this.renderList(state);
      this.renderTotals(state);
      this.renderCta(state);
      if (this.updateBar) this.updateBar();
    }

    hintFor(step, count, next) {
      if (!this.text.hint) return '';
      const need = next.min - count;
      return this.fill(this.text.hint, {
        count: need,
        label: need === 1 ? step.one : step.many,
        amount: this.formatMoney(next.amount, true),
      });
    }

    renderList(state) {
      this.listEl.querySelectorAll('[data-line]').forEach((el) => el.remove());
      this.emptyEl.hidden = state.items.length > 0;

      let lastStep = null;
      const frag = document.createDocumentFragment();
      state.items.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'cbb-line' + (lastStep !== null && lastStep !== item.step ? ' cbb-line--group-start' : '');
        li.dataset.line = item.id;
        lastStep = item.step;

        const unit = item.qty === 1 ? this.text.unitOne : this.text.unitMany;
        const discounted = item.unitDiscount > 0;
        const variant = item.variantTitle ? ` · ${this.esc(item.variantTitle)}` : '';

        li.innerHTML = `
          ${item.image ? `<img class="cbb-line__img" src="${this.esc(item.image)}" alt="" loading="lazy" width="52" height="64">` : '<span></span>'}
          <div>
            <p class="cbb-line__title">${this.esc(item.title)}${item.subtitle ? ` <span>${this.esc(item.subtitle)}</span>` : ''}${item.preorder && this.dataset.preorderLabel ? ` <em class="cbb-line__preorder">${this.esc(this.dataset.preorderLabel)}</em>` : ''}</p>
            <p class="cbb-line__unit">${item.qty} ${this.esc(unit)}${variant}</p>
            <div class="cbb-qty cbb-qty--sm">
              <button type="button" data-line-minus aria-label="Decrease quantity">&minus;</button>
              <input type="number" value="${item.qty}" min="0" max="${this.maxQty}" inputmode="numeric" data-line-qty aria-label="Quantity">
              <button type="button" data-line-plus aria-label="Increase quantity" ${item.qty >= this.maxQty ? 'disabled' : ''}>+</button>
            </div>
          </div>
          <div class="cbb-line__prices">
            ${discounted ? `<span class="cbb-line__compare">${this.formatMoney(item.price * item.qty)}</span>` : ''}
            <span class="cbb-line__price${discounted ? ' is-discounted' : ''}">${this.formatMoney(item.lineTotal)}</span>
            ${discounted && this.text.badge ? `<span class="cbb-line__badge">${this.esc(this.fill(this.text.badge, { amount: this.formatMoney(item.unitDiscount * item.qty) }))}</span>` : ''}
          </div>
          <button type="button" class="cbb-line__remove" data-line-remove aria-label="Remove ${this.esc(item.title)}">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M2 2l10 10M12 2L2 12"/></svg>
          </button>`;
        frag.appendChild(li);
      });
      this.listEl.appendChild(frag);
    }

    renderTotals(state) {
      const q = (sel) => this.querySelector(sel);
      const money = (c) => this.formatMoney(c);

      // Header: count of the first required step (or all units).
      const lead = this.steps.find((s) => s.required) || null;
      const leadCount = lead ? state.perStep.get(lead.index).count : state.units;
      const countEl = q('[data-selected-count]');
      if (countEl) {
        countEl.textContent = this.text.selected && (leadCount > 0 || state.units > 0)
          ? this.fill(this.text.selected, {
              count: leadCount,
              label: lead ? (leadCount === 1 ? lead.one : lead.many) : '',
            })
          : '';
      }
      const savingText = state.discount > 0 ? this.fill(this.text.saving, { amount: money(state.discount) }) : '';
      q('[data-head-saving]').textContent = savingText;

      const subLabel = q('[data-subtotal-label]');
      subLabel.textContent = this.fill(subLabel.dataset.tpl, { count: state.units });
      q('[data-subtotal]').innerHTML = money(state.subtotal);

      const rows = q('[data-discount-rows]');
      rows.innerHTML = '';
      state.perStep.forEach((info) => {
        if (info.discount <= 0) return;
        const div = document.createElement('div');
        div.className = 'cbb-summary__row cbb-summary__row--discount';
        const label = this.fill(this.text.discountLine, {
          label: info.step.discountLabel,
          count: info.units,
          amount: this.formatMoney(info.perUnit, true),
        });
        div.innerHTML = `<span>${this.esc(label)}</span><span>-${money(info.discount)}</span>`;
        rows.appendChild(div);
      });

      // Sidebar hint: next tier on the first tiered step that has one.
      let hint = '';
      for (const step of this.steps) {
        const info = state.perStep.get(step.index);
        const next = this.nextTier(step, info.count);
        if (next && info.count > 0) { hint = this.hintFor(step, info.count, next); break; }
      }
      q('[data-hint]').textContent = hint;

      q('[data-total]').innerHTML = money(state.total);
      q('[data-total-saving]').textContent = state.discount > 0 ? this.fill(this.text.totalSaving, { amount: money(state.discount) }) : '';

      const barCount = q('[data-bar-count]');
      if (barCount) barCount.textContent = this.fill(q('[data-subtotal-label]').dataset.tpl, { count: state.units });
      const barTotal = q('[data-bar-total]');
      if (barTotal) barTotal.innerHTML = money(state.total);
      const barSaving = q('[data-bar-saving]');
      if (barSaving) barSaving.textContent = state.discount > 0 ? this.fill(this.text.totalSaving, { amount: money(state.discount) }) : '';
    }

    renderCta(state) {
      const blocked = state.missing.length > 0 || state.items.length === 0;
      if (!this.busy) {
        this.ctaEl.disabled = blocked;
        if (this.barCtaEl) this.barCtaEl.disabled = blocked;
      }
      if (blocked && !this.msgError) {
        const step = state.missing[0];
        this.msgEl.textContent = step && this.text.required ? this.fill(this.text.required, { label: step.one, step: step.title }) : '';
        this.msgEl.classList.remove('is-error');
      } else if (!this.msgError && !this.busy) {
        this.msgEl.textContent = '';
      }
    }

    setMessage(text, isError) {
      this.msgError = Boolean(text && isError);
      this.msgEl.textContent = text || '';
      this.msgEl.classList.toggle('is-error', this.msgError);
    }

    setCtaLabel(text) {
      if (this.ctaTextEl) this.ctaTextEl.textContent = text;
      const barText = this.querySelector('[data-bar-cta-text]');
      if (barText) barText.textContent = text;
      // Phones show a short label; "Adding…" / "Added ✓" are short already.
      const barShort = this.querySelector('[data-bar-cta-short]');
      if (barShort) barShort.textContent = text === this.text.cta ? (this.dataset.ctaLabelMobile || text) : text;
    }

    // ------------------------------------------------------------ Add to cart

    async addToCart() {
      const state = this.compute();
      if (state.missing.length) {
        this.toggleStep(state.missing[0], true);
        this.measureHeader();
        state.missing[0].el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (this.busy || state.missing.length || !state.items.length) return;

      this.busy = true;
      this.ctaEl.disabled = true;
      if (this.barCtaEl) this.barCtaEl.disabled = true;
      this.setMessage('');
      this.setCtaLabel(this.text.adding);

      const bundleId = 'CB-' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 6).toUpperCase();
      const d = this.dataset;
      const items = state.items.map((item) => {
        const step = this.stepByIndex.get(item.step);
        const properties = {
          _bundle_id: bundleId,
          _bundle_name: d.bundleName || 'Bundle',
          _bundle_step: step ? step.title : String(item.step),
          _bundle_step_index: String(item.step),
          _bundle_discount: String(item.unitDiscount),
        };
        if (d.bundleProperty && d.bundleName) properties[d.bundleProperty] = d.bundleName;
        if (item.preorder && d.preorderProperty) properties[d.preorderProperty] = 'Yes';
        return { id: Number(item.id), quantity: item.qty, properties };
      });

      const afterAdd = d.afterAdd || 'drawer';
      const sections = afterAdd === 'drawer' ? this.cartSections() : [];
      const code = (d.discountCode || '').trim();

      try {
        const addBody = { items };
        if (sections.length && !code) {
          addBody.sections = sections;
          addBody.sections_url = window.location.pathname;
        }
        let parsed = await this.postJSON('cart/add.js', addBody);

        if (code) {
          const cart = await (await fetch(ROOT() + 'cart.js', { headers: { Accept: 'application/json' } })).json();
          const codes = (cart.discount_codes || []).map((c) => c.code).filter(Boolean);
          if (codes.indexOf(code) === -1) codes.push(code);
          const body = { discount: codes.join(',') };
          if (sections.length) {
            body.sections = sections;
            body.sections_url = window.location.pathname;
          }
          parsed = await this.postJSON('cart/update.js', body);
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cycle-bundle-builder' } }));

        if (afterAdd === 'checkout') { window.location.href = ROOT() + 'checkout'; return; }
        if (afterAdd === 'cart') { window.location.href = ROOT() + 'cart'; return; }

        const opened = this.applySections(parsed);
        if (!opened) {
          // No drawer on the page — the cart page is the next best thing.
          window.location.href = ROOT() + 'cart';
          return;
        }
        this.setCtaLabel(this.text.added);
        setTimeout(() => this.setCtaLabel(this.text.cta), 2000);
      } catch (error) {
        this.setMessage(error.message || 'Could not add your bundle. Please try again.', true);
        this.setCtaLabel(this.text.cta);
      } finally {
        this.busy = false;
        this.render();
      }
    }

    async postJSON(path, body) {
      const res = await fetch(ROOT() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add your bundle.');
      return data;
    }

    // Pinnacle: [{ id: element id, section: handle, selector }]; Dawn: [{ id: handle }].
    cartSections() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.getSectionsToRender !== 'function') return [];
      const handles = drawer.getSectionsToRender().map((s) => s.section || s.id);
      return handles.filter((h, i) => h && handles.indexOf(h) === i);
    }

    // Returns true when a drawer was refreshed and opened.
    applySections(parsed) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return false;
      // Dawn marks an empty drawer with .is-empty, which hides freshly rendered lines.
      drawer.classList.remove('is-empty');
      const inner = drawer.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      if (parsed && parsed.sections) {
        if (typeof drawer.renderContents === 'function') {
          drawer.renderContents(parsed);
          return true;
        }
        if (typeof drawer.getSectionsToRender === 'function') {
          drawer.getSectionsToRender().forEach((section) => {
            const host = document.getElementById(section.id);
            const html = parsed.sections[section.section];
            if (!host || !html) return;
            const target = host.querySelector(section.selector) || host;
            const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
            if (fresh) target.innerHTML = fresh.innerHTML;
          });
          if (typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
        }
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      if (typeof drawer.open === 'function') drawer.open();
      return true;
    }

    // --------------------------------------------------------------- Utils

    fill(tpl, values) {
      return String(tpl || '').replace(/\[(\w+)\]/g, (m, key) => (key in values ? values[key] : m));
    }

    esc(str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    // `short` drops ".00" — "$10 OFF" reads better than "$10.00 OFF" in labels.
    formatMoney(cents, short) {
      cents = Math.round(Number(cents) || 0);
      const fmt = this.moneyFormat;
      const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      const kind = match ? match[1] : 'amount';
      const group = (num, decimals, thousands, decimal) => {
        const fixed = (num / 100).toFixed(decimals);
        const [int, frac] = fixed.split('.');
        const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return frac ? withSep + decimal + frac : withSep;
      };
      let value;
      switch (kind) {
        case 'amount_no_decimals': value = group(cents, 0, ',', '.'); break;
        case 'amount_with_comma_separator': value = group(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = group(cents, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': value = group(cents, 2, "'", '.'); break;
        default: value = group(cents, 2, ',', '.');
      }
      if (short && cents % 100 === 0) value = value.replace(/[.,]00$/, '');
      return match ? fmt.replace(match[0], value) : value;
    }
  }

  window.customElements.define('cycle-bundle-builder', CycleBundleBuilder);
})();
